(ns ui.sticker-dialogs.mention
  "The Replicant mention sticker dialog: search an Instagram username
   (validated via Instagram, or known from the recent mentions) and add
   it as a mention sticker. With its `:sticker-dialog/mention-*` action
   handlers."
  (:require [cljs.core.async :refer [<!]]
            [storrito.editor.sticker-design :as sticker-design]
            [storrito.instagram.story.editor.tag-validation :as tag-validation]
            [storrito.instagram.story.post.util :as post-util]
            [ui.sticker-dialogs.actions :as actions]
            [ui.sticker-dialogs.recent :as recent]
            [ui.sticker-dialogs.storage :as storage]
            [ui.sticker-dialogs.ui :as ui])
  (:require-macros [cljs.core.async.macros :refer [go]]))

(def input-id
  "sticker-mention-input")

(defn initial-state
  [_w]
  {:username ""
   :status :ready
   :recent (storage/recent-mentions)})

(defn add-actions
  [{:keys [username user-id]}]
  (actions/asset-actions
    nil
    (actions/asset-tx nil
                      :mention-sticker
                      (str "@" username)
                      (assoc actions/text-sticker-base
                             :text (str "@" username)
                             :user-id user-id
                             :type "usertag"
                             :design sticker-design/default-mention-design))))

(defn add!
  "Adds the mention sticker of the validation `result` and closes."
  [{:keys [ui/event-handler]} result]
  (storage/remember-mention! result)
  (event-handler {}
                 (concat (add-actions result)
                         [[:ui/modal-close]])))

(defn answer-current?
  "Whether the answer of the validation `check-id` of `username` still
   belongs to the `dialog`: the user did not type another username
   meanwhile, and did not start a newer validation of the same one,
   whose answer a late failure of this one must not overwrite."
  [dialog check-id username]
  (and (= check-id (:check-id dialog))
       (= username (:username dialog))))

(defn check!
  "The search button: validates the typed username via Instagram, or
   the recent mentions when the username was validated before."
  [{:keys [store]}]
  (let [dialog (actions/dialog @store)
        username (:username dialog)]
    (cond
      (not (post-util/valid-usertag? username))
      (actions/swap-dialog! store assoc :status :invalid)

      ;; Typing resets the status, so a validation on its way is the one
      ;; of this username. A second Enter must not ask Instagram again:
      (= :checking (:status dialog))
      nil

      :else
      (if-let [cached (storage/find-mention username)]
        (actions/swap-dialog! store assoc
                              :status 200
                              :result cached
                              :error-message nil
                              :check-id nil)
        (let [check-id (random-uuid)]
          (actions/swap-dialog! store assoc
                                :status :checking
                                :error-message nil
                                :check-id check-id)
          (go
            (let [response (<! (tag-validation/ig-username-validation username))
                  body (:body response)
                  ok? (and (= 200 (:status response))
                           (:user-id body))]
              (when ok?
                (storage/remember-mention! body))
              (actions/swap-dialog-of!
                store
                :mention
                (fn [dialog]
                  (if (answer-current? dialog check-id username)
                    (assoc dialog
                           :status (:status response)
                           :result (when ok?
                                     {:username (:username body)
                                      :user-id (:user-id body)})
                           :error-message (:error-message body)
                           :recent (storage/recent-mentions))
                    dialog))))))))))

(defn forget!
  [{:keys [store action]}]
  (let [[_ entry] action]
    (storage/forget-mention! entry)
    (actions/swap-dialog! store assoc :recent (storage/recent-mentions))))

(defn check-button
  [{:keys [status]}]
  (if (= :checking status)
    [:span.inline-flex.items-center.justify-center.h-10.w-10
     [:div.w-5.h-5.rounded-full.border-2.border-gray-300.border-t-secondary-500.animate-spin]]
    [:button {:type "submit"
              :class (concat ui/button-base
                             (case status
                               200 ["text-white" "bg-green-600" "hover:bg-green-500" "border-transparent"]
                               (404 400 500) ["text-white" "bg-red-600" "hover:bg-red-500" "border-transparent"]
                               ui/secondary-colors))}
     [:span.items-center.inline-flex.justify-center.h-6.w-6
      [:i.fas {:class [(case status
                         200 "fa-check-circle"
                         (404 400 500) "fa-exclamation-triangle"
                         "fa-search")]}]]]))

(defn recent-chips
  [{:keys [recent]}]
  (when (seq recent)
    [:div.mt-1
     [:div.flex.flex-wrap.gap-2
      (map (fn [{:keys [username] :as entry}]
             [:span {:replicant/key (or (recent/mention-user-id entry) username)}
              (ui/chip {:label (str "@" username)
                        :title (str "Add @" username)
                        :actions [[:sticker-dialog/mention-use (recent/mention-result entry)]]
                        :remove-actions [[:sticker-dialog/mention-forget entry]]
                        :remove-title (str "Remove @" username " from cached mentions")})])
           recent)]
     [:p.mt-2.text-xs.text-gray-500
      "If a username changed, remove the saved mention with × and search again."]]))

(defn panel
  [{:keys [status error-message] :as dialog}]
  (let [incorrect? (contains? #{:invalid 404} status)]
    [:div.flex.flex-col
     (ui/title "@MENTION" {:color "#ff7b00"})
     [:form {:style {:min-width "395px"
                     :margin "10px"}
             :on {:submit [[:js/prevent-default]
                           [:sticker-dialog/mention-check]]}}
      [:label.text-gray-800.block.text-base.font-bold.mb-2 "Enter the username:"]
      [:div.flex
       [:div.relative.rounded-md.shadow-sm.max-w-full.w-full.mr-4
        (ui/text-field dialog
                       {:field :username
                        :dom-id input-id
                        :placeholder "username"
                        :autofocus? true
                        :class (concat ["mr-2" "max-w-full" "w-full" "bg-white" "rounded" "shadow-sm" "focus:shadow-md"]
                                       (if incorrect?
                                         ["border-red-300" "text-red-900" "placeholder-red-300" "focus:outline-none" "focus:ring-red-500" "focus:border-red-500"]
                                         ["border-gray-400" "text-gray-800" "focus:hover:border-gray-500"]))
                        :style {:font-family "AvenyT"}})
        (when incorrect?
          [:div.absolute.inset-y-0.right-0.pr-3.flex.items-center.pointer-events-none
           [:i.fa-solid.fa-circle-exclamation.h-5.w-5.text-red-500 {:aria-hidden "true"}]])]
       (check-button dialog)]
      (ui/error-text (cond
                       (= :invalid status)
                       "Not a valid Usertag. Only alphanumeric characters, '_' and '.' are allowed"

                       :else
                       error-message))
      (recent-chips dialog)
      [:div.flex.mt-3.justify-center
       (case status
         404 [:button {:type "button" :class ui/warning-css :disabled true} "Username not found"]
         400 [:button {:type "button" :class ui/warning-css :disabled true} "Check connection"]
         (ui/primary-button {:disabled (not= 200 status)
                             :on {:click [[:js/prevent-default]
                                          [:sticker-dialog/mention-submit]]}}
                            "Add Mention"))]]]))

(defn render
  [state]
  (when-let [dialog (actions/dialog state)]
    (panel dialog)))

(def modal
  {:modal-id :modals/editor2-usermention-modal
   :on-load (fn [_state] [[:sticker-dialog/init :mention]])
   :sticker/kind :mention
   :sticker/initial-state #'initial-state
   :render #'render})

(def register
  [modal
   (actions/handler :sticker-dialog/mention-check check!)
   (actions/handler :sticker-dialog/mention-submit
                    (fn [{:keys [store] :as w}]
                      (when-let [result (:result (actions/dialog @store))]
                        (add! w result))))
   (actions/handler :sticker-dialog/mention-use
                    (fn [{:keys [action] :as w}]
                      (add! w (second action))))
   (actions/handler :sticker-dialog/mention-forget forget!)])
